import React, { type FC } from 'react';
import WantedUnwantedToggle from './WantedUnwantedToggle';
import { DocumentTextIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { Tooltip } from 'primereact/tooltip';
import FavoriteToggle from '../common/FavoriteToggle';
import { blueButtonClasses } from '../../assets/tailwinds-common-classes';
import useGetPropertyComments from '../../hooks/api/Property/useGetPropertyComments';

export type DecisionMakingPropertyHeaderProps = {
  propertyId: string;
  savedWantedStatus: boolean;
  setIsQuickNetModalOpen: (boolean) => void;
  setIsPropertyCommentsModalOpen: (boolean) => void;
  onWantedStatusChange: (boolean) => void;
  handleSubmitDecision: () => void;
  formUpdated: boolean;
}

const DecisionMakingPropertyHeader: FC<DecisionMakingPropertyHeaderProps> = ({
  propertyId,
  setIsQuickNetModalOpen,
  setIsPropertyCommentsModalOpen,
  handleSubmitDecision,
  savedWantedStatus,
  onWantedStatusChange,
  formUpdated
}) => {
  const { commentData } = useGetPropertyComments(propertyId);
  const hasPropertyComments = !!commentData?.comment?.id;

  return (
    <div id='DecisionMakingPropertyHeader-container' className="mb-6 mt-4 px-3">
      {/* Tooltip with custom styling */}
      <Tooltip
        target=".tooltip-icon"
        className="!text-xs !py-1 !px-2 !rounded-md !bg-gray-800 !text-white shadow-md"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-y-5 items-center">
        <div className="flex flex-row gap-x-5 items-center align-middle justify-center md:justify-start w-full">
          <WantedUnwantedToggle
            savedWantedStatus={savedWantedStatus}
            onWantedStatusChange={onWantedStatusChange}
            propertyId={propertyId}
          />
          <div id='DecisionMakingPropertyHeader-favorite-toggle'
            className='cursor-pointer items-center'
            data-pr-tooltip="Favorite this property" data-pr-position="top">
            <FavoriteToggle propertyId={propertyId}/>
          </div>
          {formUpdated ? (
            <div id='save-changes-button-container'
              onClick={handleSubmitDecision}
              className={`flex items-center cursor-pointer mr-1 px-2 ${blueButtonClasses }`}
            >
              Submit Decision
            </div>
          ) : (
            <div
              id='save-changes-button-container-no-changes'
              className={'flex items-center mr-1 px-2'}
            >
              No changes to save
            </div>
          )}
        </div>

        <div className='flex items-center align-middle lg:justify-end w-full justify-center md:mt-3 mr-5'>

          <div id='DecisionMakingPropertyHeader-quick-net-sheet-button'
            onClick={() => setIsQuickNetModalOpen(true)}
            className='flex items-center cursor-pointer mr-1'
          >
            <DocumentTextIcon
              className='tooltip-icon w-6 h-6'
              data-pr-tooltip="Future Sale Quick Net Sheet"
              data-pr-position="top"
            />
          </div>

          <div id='DecisionMakingPropertyHeader-property-comments-button'
            onClick={() => setIsPropertyCommentsModalOpen(true)}
            className='flex items-center cursor-pointer ml-1'
          >
            <PencilSquareIcon
              className={`tooltip-icon w-6 h-6 ${hasPropertyComments ? 'text-green-600' : ''}`}
              data-pr-tooltip="Property Notes"
              data-pr-position="top"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DecisionMakingPropertyHeader;
